import React, { useState } from "react";
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import PropTypes from "prop-types";
import styles from "./MessageSection.module.css";

const GOOGLE_SCRIPT_URL = process.env.REACT_APP_GOOGLE_SHEETS_APP_SCRIPT_URL;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const getRecaptchaToken = async (action = "contact_form") => {
  if (!window.grecaptcha) {
    throw new Error("reCAPTCHA is not ready yet.");
  }

  await new Promise((resolve) => {
    window.grecaptcha.ready(resolve);
  });

  return window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
};

const MessageSection = ({
  className = "",
  hideTitle = false,
  id = "messagesection",
  formSource = "Website Contact Form",
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [jobType, setJobType] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState("");

  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(email).toLowerCase());
  };

  const validatePhone = (phone) => {
    const re = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;
    return re.test(String(phone));
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (newEmail && !validateEmail(newEmail)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const handlePhoneChange = (e) => {
    const newPhone = e.target.value;
    setPhone(newPhone);
    if (newPhone && !validatePhone(newPhone)) {
      setPhoneError("Please enter a valid phone number");
    } else {
      setPhoneError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    if (!validatePhone(phone)) {
      setPhoneError("Please enter a valid phone number");
      return;
    }
    if (!GOOGLE_SCRIPT_URL) {
      setStatusType("error");
      setStatusMessage(
        "Contact form is not configured yet. Please try again later."
      );
      return;
    }
    if (!RECAPTCHA_SITE_KEY) {
      setStatusType("error");
      setStatusMessage(
        "Security check is not configured yet. Please try again later."
      );
      return;
    }

    setIsSubmitting(true);
    setStatusMessage("");
    setStatusType("");
    try {
      const recaptchaToken = await getRecaptchaToken();
      const formData = new URLSearchParams({
        name,
        email,
        phone,
        jobType,
        submittedAt: new Date().toISOString(),
        source: formSource,
        formLocation: formSource,
        recaptchaToken,
        recaptchaAction: "contact_form",
      });

      const response = await fetch(GOOGLE_SCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        body: formData,
      });

      console.log("Form submitted to Google Sheets:", response);
      setStatusType("success");
      setStatusMessage("Your message has been sent successfully! We will contact you shortly.");
      setName("");
      setEmail("");
      setPhone("");
      setJobType("");
      setEmailError("");
      setPhoneError("");
    } catch (error) {
      console.error("Error sending form submission:", error);
      setStatusType("error");
      setStatusMessage("There was an error sending your message. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#f9f9f9",
      "& fieldset": { borderColor: "transparent" },
      "&:hover fieldset": { borderColor: "#e0e0e0" },
      "&.Mui-focused fieldset": { borderColor: "#000" },
      fontFamily: "Urbanist, sans-serif",
    },
    "& .MuiInputBase-input": { color: "#000", padding: "16px", fontSize: "16px", fontFamily: "Urbanist, sans-serif" },
    "& .MuiInputLabel-root": { fontFamily: "Urbanist, sans-serif" },
    width: "100%",
  };

  return (
    <section
      id={id}
      className={[styles.letsTalk, className].join(" ")}
    >
      {!hideTitle && (
        <div className={styles.contactUsWrapper}>
          <h1 className={styles.contactUs}>Contact Us</h1>
        </div>
      )}
      {statusMessage ? (
        <div
          className={[
            styles.statusMessage,
            statusType === "success" ? styles.statusSuccess : styles.statusError,
          ].join(" ")}
          role="status"
          aria-live="polite"
        >
          {statusMessage}
        </div>
      ) : null}
      <div className={styles.form}>
        <TextField
          className={styles.inputFields}
          placeholder="Full Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          variant="outlined"
          sx={inputStyles}
        />
        <TextField
          className={styles.inputFields}
          placeholder="Email"
          type="email"
          value={email}
          onChange={handleEmailChange}
          error={!!emailError}
          helperText={emailError}
          variant="outlined"
          sx={inputStyles}
        />
        <TextField
          className={styles.inputFields}
          placeholder="Phone Number"
          type="tel"
          value={phone}
          onChange={handlePhoneChange}
          error={!!phoneError}
          helperText={phoneError}
          variant="outlined"
          sx={inputStyles}
        />
        <FormControl
          className={styles.inputFields}
          variant="outlined"
          sx={inputStyles}
        >
          <InputLabel id="job-type-label" sx={{ fontFamily: "Urbanist, sans-serif" }}>Looking for</InputLabel>
          <Select
            labelId="job-type-label"
            value={jobType}
            onChange={(e) => setJobType(e.target.value)}
            label="Looking for"
            sx={{ "& .MuiSelect-select": { padding: "16px", fontFamily: "Urbanist, sans-serif" } }}
            MenuProps={{
              PaperProps: {
                sx: {
                  "& .MuiMenuItem-root": {
                    fontFamily: "Urbanist, sans-serif",
                  },
                },
              },
            }}
          >
            <MenuItem value="Commercial New Job">Commercial-New Job</MenuItem>
            <MenuItem value="Commercial Renovation">
              Commercial-Renovation
            </MenuItem>
            <MenuItem value="Residential New Job">Residential-New Job</MenuItem>
            <MenuItem value="Residential Renovation">
              Residential-Renovation
            </MenuItem>
          </Select>
        </FormControl>
      </div>
      <div className={styles.submit}>
        <Button
          type="submit"
          onClick={handleSubmit}
          className={styles.btn}
          disabled={isSubmitting}
          disableElevation
          variant="contained"
          sx={{
            backgroundColor: "#000",
            color: "#fff",
            fontSize: "15px",
            fontWeight: "600",
            fontFamily: "Urbanist, sans-serif",
            borderRadius: "12px",
            textTransform: "none",
            "&:hover": { backgroundColor: "#333" },
            width: "100%",
            height: "54px",
          }}
        >
          {isSubmitting ? "Sending..." : "Send Message"}
        </Button>
      </div>
    </section>
  );
};

MessageSection.propTypes = {
  className: PropTypes.string,
  hideTitle: PropTypes.bool,
  id: PropTypes.string,
  formSource: PropTypes.string,
};

export default MessageSection;
